/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * useNotification Hook
 * ============================================================================
 *
 * Convenience hook for showing toast notifications from any component.
 * Provides type-safe methods for success, error, warning, and info notifications.
 *
 * ============================================================================
 */

import { useCallback } from 'react';
import { useNotificationStore, DEFAULT_DURATIONS } from '@/stores/notificationStore';
import type { NotificationType } from '@/stores/notificationStore';
import type { ApiError } from '@/services/api/types';
import { getErrorMessage, isApiError } from '@/services/api/errors';

/**
 * Notification options
 */
interface NotificationOptions {
  /** Custom duration in ms (undefined = use default for type) */
  duration?: number;
  /** Field-level errors for inline display */
  fieldErrors?: Record<string, string>;
}

/**
 * useNotification hook return type
 */
interface UseNotificationReturn {
  /** Show a success notification (auto-dismisses in 5s) */
  success: (message: string, title?: string, options?: NotificationOptions) => string;
  /** Show an error notification (manual close only by default) */
  error: (message: string, title?: string, options?: NotificationOptions) => string;
  /** Show a warning notification (auto-dismisses in 8s) */
  warning: (message: string, title?: string, options?: NotificationOptions) => string;
  /** Show an info notification (auto-dismisses in 5s) */
  info: (message: string, title?: string, options?: NotificationOptions) => string;
  /** Show a notification from an API error (extracts message and fieldErrors) */
  fromApiError: (error: unknown, title?: string) => string;
  /** Dismiss a specific notification by ID */
  dismiss: (id: string) => void;
  /** Dismiss all notifications */
  dismissAll: () => void;
  /** Dismiss all notifications of a specific type */
  dismissByType: (type: NotificationType) => void;
}

/**
 * Default titles for notification types
 */
const DEFAULT_TITLES: Record<NotificationType, string> = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
};

/**
 * useNotification hook
 *
 * @example
 * const { success, error, fromApiError, dismiss } = useNotification();
 *
 * // Show success notification
 * success('Profile updated successfully!');
 *
 * // Show error with custom title
 * error('Failed to save changes', 'Save Error');
 *
 * // Handle API error
 * try {
 *   await saveData();
 * } catch (err) {
 *   fromApiError(err);
 * }
 *
 * // Error with field mapping (for inline display)
 * error('Validation failed', 'Error', {
 *   fieldErrors: { email: 'Email already exists' }
 * });
 */
export function useNotification(): UseNotificationReturn {
  const { addNotification, removeNotification, clearAll, clearByType } =
    useNotificationStore();

  /**
   * Generic notification function
   */
  const notify = useCallback(
    (
      type: NotificationType,
      message: string,
      title?: string,
      options?: NotificationOptions
    ): string => {
      return addNotification({
        type,
        title: title || DEFAULT_TITLES[type],
        message,
        duration: options?.duration !== undefined ? options.duration : DEFAULT_DURATIONS[type],
        fieldErrors: options?.fieldErrors,
      });
    },
    [addNotification]
  );

  /**
   * Show success notification
   */
  const success = useCallback(
    (message: string, title?: string, options?: NotificationOptions): string => {
      return notify('success', message, title, options);
    },
    [notify]
  );

  /**
   * Show error notification
   */
  const error = useCallback(
    (message: string, title?: string, options?: NotificationOptions): string => {
      return notify('error', message, title, options);
    },
    [notify]
  );

  /**
   * Show warning notification
   */
  const warning = useCallback(
    (message: string, title?: string, options?: NotificationOptions): string => {
      return notify('warning', message, title, options);
    },
    [notify]
  );

  /**
   * Show info notification
   */
  const info = useCallback(
    (message: string, title?: string, options?: NotificationOptions): string => {
      return notify('info', message, title, options);
    },
    [notify]
  );

  /**
   * Show notification from API error
   * Extracts error message and fieldErrors automatically
   */
  const fromApiError = useCallback(
    (err: unknown, title?: string): string => {
      if (isApiError(err)) {
        // Get user-friendly message from error code mapping
        const message = err.message || getErrorMessage(err.code);

        return notify('error', message, title, {
          fieldErrors: err.fieldErrors,
        });
      }

      // Handle generic errors
      const message =
        err instanceof Error ? err.message : 'An unexpected error occurred';
      return notify('error', message, title);
    },
    [notify]
  );

  /**
   * Dismiss a specific notification
   */
  const dismiss = useCallback(
    (id: string): void => {
      removeNotification(id);
    },
    [removeNotification]
  );

  /**
   * Dismiss all notifications
   */
  const dismissAll = useCallback((): void => {
    clearAll();
  }, [clearAll]);

  /**
   * Dismiss all notifications of a type
   */
  const dismissByType = useCallback(
    (type: NotificationType): void => {
      clearByType(type);
    },
    [clearByType]
  );

  return {
    success,
    error,
    warning,
    info,
    fromApiError,
    dismiss,
    dismissAll,
    dismissByType,
  };
}

export default useNotification;
